import { useState, useRef, useMemo } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";
import { SetLiveEvent } from "models/event";
import { H4 } from "melodies-source/Text";
import { ReactComponent as CircleCheckedIcon } from "assets/svg/circle-checked.svg";
import { ReactComponent as DocumentIcon } from "assets/svg/document.svg";
import { MenuButton } from "Components/MenuButton";
import { ReactComponent as KebabMenuIcon } from "assets/svg/kebab-menu.svg";
import { ReactComponent as GearIcon } from "assets/svg/gear.svg";
import { SvgShowLarge } from "melodies-source/Svgs/ShowLarge";
import { ReactComponent as TrashIcon } from "assets/svg/trash.svg";
import { useHistory, useParams } from "react-router-dom";
import { Modal } from "melodies-source/Modal";
import { toast } from "react-hot-toast";
import { Confirmation } from "melodies-source/Confirmation";
import { useCustomAppContext } from "contexts/CustomAppContext";
import { useUser } from "auth";
import { ReactComponent as AddUserIcon } from "assets/svg/add-user.svg";
import { EditAdditionalGroups } from "./EditAdditionalGroups";
import { useArtistContext } from "Components/ArtistProvider";
import { useBackstageUrl } from "hooks/useBackstageUrl";
import { useTimeContext } from "Components";
import { EventCardBase, MenuContainer } from "./EventCardBase";
import { SvgOpen } from "melodies-source/Svgs/Open";
import { SvgEditAlt } from "melodies-source/Svgs/EditAlt";
import { Button } from "melodies-source/Button";
import { useMediaQuery } from "react-responsive";
import { mediaQueryBreakpoints } from "melodies-source/Theme/mediaQueries";

export interface SetLiveEventCardProps {
  event: SetLiveEvent;
  showType: "upcoming" | "past";
  onDelete?: (id: string) => Promise<boolean>;
  onUpdate?: (id: string, data: Partial<SetLiveEvent>) => void;
}

type MenuItem = {
  label: string;
  icon: { position: "left"; element: React.ReactElement };
  onClick: () => void;
  visible: boolean;
};

export const SetLiveEventCard: React.FC<SetLiveEventCardProps> = ({
  event,
  showType,
  onDelete,
  onUpdate,
}) => {
  const { artistId } = useParams<{ artistId: string }>();
  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(false);
  const { claims } = useUser();
  const [editAdditionalGroupsOpen, setEditAdditionalGroupsOpen] =
    useState(false);
  const statusRef = useRef<string>("");
  const { customApp, language } = useCustomAppContext();
  const { logAction } = useArtistContext();
  const { goToBackstageUrl } = useBackstageUrl();
  const { time } = useTimeContext();
  const isMobile = !useMediaQuery({
    query: mediaQueryBreakpoints.desktop,
  });

  const isPublished = event.status === "published";
  const live =
    event.startsAt <= time.toMillis() && !event.endedAt && isPublished;

  const handleDelete = async () => {
    if (!onDelete) return;
    statusRef.current = "deleting";
    const req = await onDelete(event.id);
    if (req) {
      await logAction("portal_delete_event", { event: event.id });
      statusRef.current = "";
      toast.success(`${language.event.type.singular} deleted`);
      setModalOpen(false);
    }
  };

  const handleCloseModal = () => {
    if (statusRef.current !== "deleting") {
      setModalOpen(false);
    }
  };

  const isOwner = artistId === event.groupId;
  const isPastShow = showType === "past";

  const menuItems = useMemo(() => {
    const items: MenuItem[] = [
      {
        label: "Edit",
        icon: {
          position: "left",
          element: <GearIcon color="var(--text-color)" />,
        },
        onClick: () => {
          window.location.href = `/set-live-creator/b/${event.id}/editor?ref=ap`;
        },
        visible:
          (isPublished && isOwner && !customApp && !isPastShow) ||
          (claims.admin && isPastShow),
      },
      {
        label: "Add Artist",
        icon: {
          position: "left",
          element: <AddUserIcon color="var(--text-color)" />,
        },
        onClick: () => setEditAdditionalGroupsOpen(true),
        visible: isPublished && claims.admin && !customApp && !isPastShow,
      },
      {
        label: `${language.event.audience.singular} View`,
        icon: { position: "left", element: <SvgShowLarge /> },
        onClick: () =>
          window.open(
            `${customApp?.fanAppUrl || process.env.REACT_APP_SL_URL}/e/${
              event.id
            }`,
            "_blank",
          ),
        visible: isPublished && !isPastShow,
      },
      {
        label: `${language.event.owner.singular} View`,
        icon: { position: "left", element: <SvgOpen /> },
        onClick: () => goToBackstageUrl(`/${event.id}`),
        visible: isPublished && !isPastShow,
      },
      {
        label: "Delete",
        icon: { position: "left", element: <TrashIcon /> },
        onClick: () => setModalOpen(true),
        visible: isOwner && !isPastShow,
      },
    ];

    return items
      .filter((item) => item.visible)
      .map(({ visible, ...rest }) => rest);
  }, [isPublished, isOwner, isPastShow, customApp, claims.admin, event.id]);

  const menuElement = menuItems.length ? (
    <MenuContainer>
      <MenuButton
        menu={{ position: "right", width: "200px", items: menuItems }}
        textButton
      >
        <KebabMenuIcon color="var(--text-color)" />
      </MenuButton>
    </MenuContainer>
  ) : null;

  const statusBadge =
    showType === "upcoming" && !live ? (
      <Status status={event.status}>
        {isPublished ? <CircleCheckedIcon /> : <DocumentIcon />}
        {event.status}
      </Status>
    ) : null;

  const actions = (
    <>
      {showType === "past" && (
        <Button
          variant="primary"
          onClick={() =>
            history.push(`/${artistId}/reporting/set-live/shows/${event.id}`)
          }
        >
          View Details
        </Button>
      )}
      {showType === "upcoming" && (
        <ButtonContainer>
          {live && (
            <Button
              variant="outlined"
              onClick={() => goToBackstageUrl(`/${event.id}`)}
            >
              {language.event.owner.singular} View
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => {
              window.location.href = `/set-live-creator/b/${event.id}/editor?ref=ap`;
            }}
            leftIcon={<SvgEditAlt />}
          >
            Edit
          </Button>
        </ButtonContainer>
      )}
    </>
  );

  return (
    <EventCardBase
      event={event}
      showType={showType}
      live={live}
      menu={menuElement}
      statusBadge={statusBadge}
      actions={actions}
    >
      {modalOpen && (
        <Modal
          isOpen={modalOpen}
          header={`Delete Upcoming ${language.event.type.singular}`}
          onClose={handleCloseModal}
        >
          <Confirmation
            confirmLabel="Delete"
            confirmVariant="destructive"
            loading={statusRef.current === "deleting"}
            onCancel={handleCloseModal}
            onConfirm={handleDelete}
          >
            {(() => {
              switch (statusRef.current) {
                case "deleting":
                  return (
                    <p>
                      Deleting {language.event.type.singular.toLowerCase()}...
                    </p>
                  );
                case "error":
                  return (
                    <p style={{ color: "var(--destructive-color)" }}>
                      There was an error removing the event.
                    </p>
                  );
                default:
                  return (
                    <>
                      Are you sure you wish to delete the{" "}
                      {language.event.type.singular.toLowerCase()} for "
                      {event.venue}" on{" "}
                      {DateTime.fromMillis(event.startsAt, {
                        zone: event.timeZoneName,
                      }).toLocaleString(DateTime.DATETIME_FULL)}
                      ?
                    </>
                  );
              }
            })()}
          </Confirmation>
        </Modal>
      )}
      <EditAdditionalGroups
        eventId={event.id}
        open={editAdditionalGroupsOpen}
        onUpdate={onUpdate}
        onClose={() => setEditAdditionalGroupsOpen(false)}
      />
    </EventCardBase>
  );
};

interface StatusProps {
  status: SetLiveEvent["status"];
}

const Status = styled(H4)<StatusProps>`
  display: flex;
  align-items: center;
  gap: 5px;
  text-transform: uppercase;
  margin-top: 3px;

  color: ${({ status }) => (status === "published" ? "#1DAB98" : "#FC6716")};

  ${({ theme }) => theme.media.mobile} {
    font-size: 14px;
    justify-content: center;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-grow: 1;

  ${({ theme }) => theme.mediaQueries.menuCollapsed} {
    flex-direction: column;
    align-items: flex-end;
  }
  ${({ theme }) => theme.media.mobile} {
    align-items: stretch;
  }
`;
